import React from 'react';
import { ChevronDown, Check, Globe, Building } from 'lucide-react';
import { useAirline, UserAllowedAirline } from '../contexts/AirlineContext';

// =====================================================================
// Shared Helpers
// =====================================================================

const getAirlineIcon = (code: string, size: 'sm' | 'md' = 'md') => {
  const sizeNum = size === 'sm' ? 16 : 20;
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  if (code === 'GLOBAL') {
    return <Globe size={sizeNum} className={`${sizeClass} text-sky-600`} />;
  }
  return <Building size={sizeNum} className={`${sizeClass} text-navy`} />;
};

const getRoleBadge = (role?: string) => {
  if (!role) return null;
  const colors = {
    admin: 'bg-red-100 text-red-800',
    editor: 'bg-blue-100 text-blue-800',
    viewer: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[role as keyof typeof colors]}`}>
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
};

// =====================================================================
// Shared Dropdown List
// =====================================================================

interface AirlineDropdownListProps {
  airlines: UserAllowedAirline[];
  selectedAirlineId: string;
  onSelect: (airline: UserAllowedAirline) => void;
  /** 'down' for top-nav placement, 'up' for bottom-nav placement */
  direction?: 'down' | 'up';
  align?: 'left' | 'right';
}

const AirlineDropdownList: React.FC<AirlineDropdownListProps> = ({
  airlines,
  selectedAirlineId,
  onSelect,
  direction = 'down',
  align = 'right',
}) => {
  const positionClass = direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2';
  const alignClass = align === 'left' ? 'left-0' : 'right-0';

  return (
    <div className={`absolute ${alignClass} ${positionClass} z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg`}>
      <div className="p-2">
        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Your Airlines
        </div>

        {airlines.map((airline) => {
          const isSelected = airline.airline_id === selectedAirlineId;
          return (
            <button
              key={airline.airline_id}
              onClick={() => onSelect(airline)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                isSelected ? 'bg-sky-50 text-sky-900' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex-shrink-0">
                {getAirlineIcon(airline.code)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{airline.name}</span>
                  {airline.is_default && (
                    <span className="text-xs text-gray-500">(Default)</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500">{airline.code}</span>
                  {getRoleBadge(airline.role)}
                </div>
              </div>
              {isSelected && (
                <Check size={20} className="w-5 h-5 text-sky-600 flex-shrink-0" />
              )}
            </button>
          );
        })}

        {airlines.length === 0 && (
          <div className="px-3 py-8 text-center text-sm text-gray-500">
            No airlines available
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Full Selector (desktop top-nav)
// =====================================================================

export const AirlineSelector: React.FC = () => {
  const { selectedAirline, sessionAirlines, setSelectedAirline, isLoading } = useAirline();
  const isSingleAirline = sessionAirlines.length <= 1;
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading || !selectedAirline) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-navy/20 rounded-lg animate-pulse shadow-sm">
        <div className="w-4 h-4 bg-navy/20 rounded" />
        <div className="w-32 h-4 bg-navy/20 rounded" />
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !isSingleAirline && setIsOpen(!isOpen)}
        disabled={isSingleAirline}
        className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-navy bg-white border-2 border-navy/20 rounded-lg transition-all shadow-sm disabled:opacity-60 disabled:cursor-default hover:enabled:bg-navy/5 hover:enabled:border-navy/40 focus:enabled:outline-none focus:enabled:ring-2 focus:enabled:ring-navy/30"
        aria-label="Select airline"
        aria-expanded={isSingleAirline ? undefined : isOpen}
        aria-disabled={isSingleAirline}
      >
        {getAirlineIcon(selectedAirline.code, 'sm')}
        <span className="max-w-[160px] truncate">{selectedAirline.name}</span>
        {getRoleBadge(selectedAirline.role)}
        <ChevronDown
          size={16}
          className={`w-4 h-4 transition-transform ${isSingleAirline ? 'text-navy/30' : `text-navy/60 ${isOpen ? 'rotate-180' : ''}`}`}
        />
      </button>

      {isOpen && !isSingleAirline && (
        <AirlineDropdownList
          airlines={sessionAirlines}
          selectedAirlineId={selectedAirline.airline_id}
          onSelect={(airline) => { setSelectedAirline(airline); setIsOpen(false); }}
          direction="down"
          align="right"
        />
      )}
    </div>
  );
};

// =====================================================================
// Compact Selector (mobile bottom-nav)
// =====================================================================

export const AirlineSelectorCompact: React.FC = () => {
  const { selectedAirline, sessionAirlines, setSelectedAirline } = useAirline();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!selectedAirline) return null;

  const isSingleAirline = sessionAirlines.length <= 1;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !isSingleAirline && setIsOpen(!isOpen)}
        disabled={isSingleAirline}
        className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md disabled:opacity-60 disabled:cursor-default hover:enabled:bg-gray-50"
        aria-label="Select airline"
      >
        {getAirlineIcon(selectedAirline.code, 'sm')}
        <span className="font-medium">{selectedAirline.code}</span>
        {!isSingleAirline && <ChevronDown size={16} className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
      </button>

      {isOpen && !isSingleAirline && (
        <AirlineDropdownList
          airlines={sessionAirlines}
          selectedAirlineId={selectedAirline.airline_id}
          onSelect={(airline) => { setSelectedAirline(airline); setIsOpen(false); }}
          direction="up"
          align="left"
        />
      )}
    </div>
  );
};
